import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { IconBadge } from "./IconBadge";
import { TagChip } from "./TagChip";
import { WallpaperModal } from "./WallpaperModal";
import { SharePanel } from "./SharePanel";
import { CurateQuoteButton } from "./curation/CurateQuoteButton";
import { usePlausible } from "@/hooks/usePlausible";
import {
  useToggleQuoteFavorite,
  useGetAuthorBio,
  getGetAuthorBioQueryKey,
  getGetQuotesFavoritesQueryKey,
  getGetQuotesRecommendationsQueryKey,
  getGetProfileQuoteClustersQueryKey,
  useDismissQuote,
} from "@workspace/api-client-react";

interface Quote {
  id: number;
  text: string;
  author: string;
  era?: string | null;
  year?: string | null;
  tags: string[];
  reasonSnippet?: string | null;
  score?: number | null;
}

interface QuoteCardProps {
  quote: Quote;
  featured?: boolean;
  initialFavorite?: boolean;
  /**
   * When true the entire card body is clickable to flip to the author bio.
   * The flip button in the footer is always visible regardless of this prop.
   */
  enableFlip?: boolean;
  /**
   * When provided, shows a "Not for me" dismiss button in the footer.
   */
  onDismiss?: (quoteId: number) => void;
  /**
   * When provided alongside onDismiss, the card stays visible after dismissal
   * and shows this text as a confirmation instead of disappearing. Intended for
   * the daily quote card where there is no list to scroll past.
   */
  dismissConfirmation?: string;
  /**
   * When provided and the current user is a guest (not signed in), calling the
   * save button will invoke this handler instead of the favorite mutation.
   * Intended for the public landing page: store a pending save and open the
   * auth modal so the save completes automatically after sign-in/sign-up.
   */
  onGuestSave?: (quoteId: number) => void;
}

function matchLabel(score: number): { text: string; className: string } {
  if (score >= 0.85) return { text: "High match", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (score >= 0.72) return { text: "Good match", className: "bg-sky-50 text-sky-700 border-sky-200" };
  return { text: "Match", className: "bg-stone-50 text-stone-500 border-stone-200" };
}

function BioSkeleton() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-3.5 w-full rounded-full bg-stone-100" />
      <div className="h-3.5 w-5/6 rounded-full bg-stone-100" />
      <div className="h-3.5 w-4/6 rounded-full bg-stone-100" />
    </div>
  );
}

const footerBtn =
  "rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-500 transition hover:border-stone-300 hover:bg-stone-50 hover:text-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1";

export function QuoteCard({
  quote,
  featured = false,
  initialFavorite = false,
  enableFlip = false,
  onDismiss,
  dismissConfirmation,
  onGuestSave,
}: QuoteCardProps) {
  const { currentUser } = useAuth();
  const [optimisticFavorite, setOptimisticFavorite] = useState(initialFavorite);
  const [dismissed, setDismissed] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  // Whether the 3D flip context (perspective + preserve-3d + back face) is
  // mounted. Kept off at rest: any element that creates its own GPU compositing
  // layer — `transform-style:preserve-3d` AND a plain `backface-visibility:hidden`
  // — paints on its own layer and, in Chrome, covers fixed-position siblings
  // (e.g. the floating Feedback button and the portaled feedback modal)
  // regardless of z-index. So both are gated behind this flag: turned on only
  // while a flip is active and torn down once the card settles back to the front.
  const [flip3d, setFlip3d] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(false);
  const [showWallpaper, setShowWallpaper] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [clampedHeight, setClampedHeight] = useState<number | null>(null);
  const quoteTextRef = useRef<HTMLParagraphElement>(null);
  const queryClient = useQueryClient();
  const { trackEvent } = usePlausible();

  useLayoutEffect(() => {
    const el = quoteTextRef.current;
    if (!el || expanded) return;
    const measure = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
      // Reserve the full clamp height (line-height * number of clamped lines)
      // even when this particular quote is short, so every card in a grid
      // reserves the same amount of vertical space for its quote text.
      const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
      const lines = featured ? 6 : 4;
      if (!Number.isNaN(lineHeight)) setClampedHeight(lineHeight * lines);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [quote.text, featured, expanded]);

  const { mutate: dismissQuote, isPending: isDismissPending } = useDismissQuote({
    mutation: {
      onMutate: () => {
        setDismissed(true);
        onDismiss?.(quote.id);
      },
      onError: () => {
        setDismissed(false);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetQuotesRecommendationsQueryKey() });
      },
    },
  });

  const { mutate: toggleFavorite, isPending } = useToggleQuoteFavorite({
    mutation: {
      onMutate: () => setOptimisticFavorite((prev) => !prev),
      onError: () => setOptimisticFavorite((prev) => !prev),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetQuotesFavoritesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetQuotesRecommendationsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetProfileQuoteClustersQueryKey() });
      },
    },
  });

  const bioParams = { exclude: quote.id };
  const { data: bioData, isLoading: isBioLoading } = useGetAuthorBio(
    encodeURIComponent(quote.author),
    bioParams,
    { query: { enabled: bioEnabled, queryKey: getGetAuthorBioQueryKey(encodeURIComponent(quote.author), bioParams) } },
  );

  function flipToBack(e?: React.MouseEvent) {
    e?.stopPropagation();
    setFlip3d(true);
    setBioEnabled(true);
    setIsFlipped(true);
  }

  function flipToFront(e?: React.MouseEvent) {
    e?.stopPropagation();
    setIsFlipped(false);
    // Fallback teardown: if onTransitionEnd is ever suppressed (rare browser
    // edge case), still drop the 3D context shortly after the 500ms rotation
    // so the card cannot keep covering fixed-position elements.
    window.setTimeout(() => setFlip3d(false), 700);
  }

  const authorHref = `/authors/${encodeURIComponent(quote.author)}`;
  const match = quote.score != null ? matchLabel(quote.score) : null;
  const bodyPadding = featured ? "px-8 pt-8" : "px-5 pt-5";

  if (dismissed) {
    if (dismissConfirmation) {
      return (
        <div className="flex min-h-[180px] items-center justify-center rounded-3xl border border-stone-200 bg-white p-12 shadow-sm">
          <p className="text-center text-sm text-stone-400">{dismissConfirmation}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <>
    <div className={flip3d ? "[perspective:1200px]" : undefined} data-testid={`quote-card-${quote.id}`}>
      {/* Rotating inner */}
      <div
        className={`relative transition-[transform] duration-500 ease-in-out ${flip3d ? "[transform-style:preserve-3d]" : ""} ${isFlipped ? "[transform:rotateY(180deg)]" : ""}`}
        style={{ minHeight: isFlipped ? "380px" : undefined }}
        onTransitionEnd={(e) => {
          // Once the card has rotated back to the front, drop the 3D context
          // so it stops covering fixed-position elements.
          if (e.currentTarget === e.target && e.propertyName === "transform" && !isFlipped) {
            setFlip3d(false);
          }
        }}
      >
        {/* ── Front face ──────────────────────────────────────────── */}
        <div
          className={`w-full overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md ${flip3d ? "[backface-visibility:hidden]" : ""} ${enableFlip ? "cursor-pointer" : ""}`}
          onClick={enableFlip ? flipToBack : undefined}
          title={enableFlip ? "Click to learn about the author" : undefined}
        >
          {/* Body */}
          <div className={bodyPadding}>
            {/* Header: badge + match only */}
            <div className="mb-5 flex items-center gap-2">
              <IconBadge>{featured ? "\u201C" : "Q"}</IconBadge>
              {match && (
                <span
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${match.className}`}
                  data-testid={`match-label-${quote.id}`}
                >
                  {match.text}
                </span>
              )}
            </div>

            {/* Quote text */}
            <div
              className="relative"
              style={!expanded && clampedHeight ? { minHeight: `${clampedHeight}px` } : undefined}
            >
              <p
                ref={quoteTextRef}
                className={`font-serif leading-relaxed text-stone-900 ${featured ? "text-3xl md:text-4xl" : "text-lg"} ${!expanded ? (featured ? "line-clamp-6" : "line-clamp-4") : ""}`}
              >
                "{quote.text}"
              </p>
              {!expanded && isOverflowing && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" />
              )}
            </div>
            {/* Reserve the "Read more" row's height on every card, even when
                this quote doesn't overflow, so cards stay a uniform size
                whether or not the control is shown. */}
            <div className="mt-1.5 h-5">
              {(isOverflowing || expanded) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  className="text-sm font-medium text-stone-500 transition hover:text-stone-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 rounded"
                  data-testid={`btn-read-more-${quote.id}`}
                >
                  {expanded ? "Show less" : "Read more"}
                </button>
              )}
            </div>

            {/* Author + era + tags */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 pt-4">
              <div>
                <Link
                  href={authorHref}
                  onClick={(e) => e.stopPropagation()}
                  className="-mx-1 rounded-md px-1 font-semibold text-stone-900 transition-colors hover:bg-stone-100"
                  data-testid={`author-link-${quote.id}`}
                >
                  {quote.author}
                </Link>
                <p className="text-sm text-stone-500">
                  {quote.era}
                  {quote.year ? ` · ${quote.year}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {quote.tags.map((tag) => (
                  <Link
                    key={tag}
                    href={`/tags/${encodeURIComponent(tag)}`}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`tag-link-${tag}`}
                  >
                    <TagChip>{tag}</TagChip>
                  </Link>
                ))}
              </div>
            </div>

            {/* Reason snippet */}
            {quote.reasonSnippet && (
              <div className="mt-4 rounded-2xl bg-stone-50 p-3 text-sm leading-relaxed text-stone-600">
                ✦ Because you saved &ldquo;{quote.reasonSnippet}&rdquo;
              </div>
            )}
          </div>

          {/* ── Footer action bar ─────────────────────────────────── */}
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-y-2 border-t border-stone-100 bg-stone-50 px-3 py-2.5 sm:px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left: dismiss (Discover only) */}
            {onDismiss ? (
              <button
                onClick={(e) => { e.stopPropagation(); dismissQuote({ id: quote.id }); }}
                disabled={isDismissPending}
                className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-stone-200 bg-white px-2.5 py-1.5 text-[10px] font-medium text-stone-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-500 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100"
                data-testid={`btn-dismiss-${quote.id}`}
                title="Not for me"
              >
                ✕ Not for me
              </button>
            ) : (
              <span />
            )}

            {/* Right: share + wallpaper + about + save */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <CurateQuoteButton
                quoteId={quote.id}
                className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 transition hover:bg-amber-100 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-1"
              />
              <SharePanel
                quoteId={quote.id}
                text={quote.text}
                author={quote.author}
                triggerClassName={footerBtn}
                triggerLabel="↗ Share"
                data-testid={`btn-share-${quote.id}`}
              />
              <button
                onClick={(e) => { e.stopPropagation(); setShowWallpaper(true); }}
                className={footerBtn}
                data-testid={`btn-wallpaper-${quote.id}`}
                title="Save as wallpaper"
              >
                ⬛ Wallpaper
              </button>
              <button
                onClick={flipToBack}
                className={footerBtn}
                data-testid={`btn-about-${quote.id}`}
                title="About the author"
              >
                ✦ About
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!currentUser && onGuestSave) {
                    onGuestSave(quote.id);
                    return;
                  }
                  if (!optimisticFavorite) trackEvent("Quote Saved");
                  toggleFavorite({ id: quote.id });
                }}
                disabled={isPending}
                className={
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-60 disabled:active:scale-100 " +
                  (optimisticFavorite
                    ? "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                    : "border-stone-200 bg-white text-stone-500 hover:border-rose-200 hover:text-rose-600")
                }
                data-testid={`btn-favorite-${quote.id}`}
              >
                {optimisticFavorite ? "♥ Saved" : "♡ Save"}
              </button>
            </div>
          </div>
        </div>

        {/* ── Back face (only mounted while a flip is active) ──────── */}
        {flip3d && (
        <div
          className="absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden] rounded-3xl border border-stone-200 bg-stone-50 shadow-sm overflow-y-auto cursor-pointer"
          onClick={flipToFront}
          data-testid={`quote-card-back-${quote.id}`}
        >
          <div className="p-5 h-full flex flex-col gap-5">
            {/* Back header */}
            <div className="flex items-center justify-between">
              <button
                onClick={flipToFront}
                className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
                data-testid={`btn-flip-back-${quote.id}`}
              >
                ← Back
              </button>
              <Link
                href={authorHref}
                onClick={(e) => e.stopPropagation()}
                className="text-sm font-semibold text-stone-900 hover:underline underline-offset-2"
              >
                {quote.author}
              </Link>
            </div>

            {/* Author bio */}
            <div
              className="rounded-2xl border border-stone-200 bg-white p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">About the author</p>
              {isBioLoading ? (
                <BioSkeleton />
              ) : bioData?.bio ? (
                <p className="text-sm leading-relaxed text-stone-700">{bioData.bio}</p>
              ) : (
                <p className="text-sm text-stone-400 italic">Bio coming soon.</p>
              )}
            </div>

            {/* More from this author */}
            {!isBioLoading && bioData && bioData.otherQuotes.length > 0 && (
              <div onClick={(e) => e.stopPropagation()}>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-400">
                  More from {quote.author}
                </p>
                <div className="space-y-3">
                  {bioData.otherQuotes.map((q) => (
                    <div key={q.id} className="rounded-2xl border border-stone-200 bg-white p-3">
                      <p className="text-sm font-serif leading-snug text-stone-800 line-clamp-3">
                        &ldquo;{q.text}&rdquo;
                      </p>
                      {q.year && (
                        <p className="mt-1.5 text-xs text-stone-400">{q.year}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>

    <WallpaperModal
      open={showWallpaper}
      onOpenChange={setShowWallpaper}
      text={quote.text}
      author={quote.author}
    />
    </>
  );
}
